import { useState, useEffect } from 'react';
import { Cookie, X, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';

const CookieConsent = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Toujours activé
    analytics: false,
    functional: false,
    advertising: false,
  });

  useEffect(() => {
    const checkAndShow = () => {
      // Vérifier si l'utilisateur a déjà confirmé son âge
      const ageVerified = localStorage.getItem('ageVerified');
      if (!ageVerified) {
        // Ne pas afficher le popup de cookies si l'âge n'est pas vérifié
        return;
      }

      // Vérifier si l'utilisateur a déjà donné son consentement
      const cookieConsent = localStorage.getItem('cookieConsent');
      if (!cookieConsent) {
        setShowPopup(true);
      } else {
        // Charger les préférences sauvegardées
        const savedPreferences = localStorage.getItem('cookiePreferences');
        if (savedPreferences) {
          setPreferences(JSON.parse(savedPreferences));
        }
      }
    };

    // Vérifier immédiatement
    checkAndShow();

    // Écouter les changements dans localStorage (pour détecter la confirmation d'âge)
    const handleStorageChange = () => {
      checkAndShow();
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Vérifier périodiquement (pour le même onglet)
    const interval = setInterval(() => {
      checkAndShow();
    }, 500);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      functional: true,
      advertising: true,
    };
    setPreferences(allAccepted);
    localStorage.setItem('cookieConsent', 'true');
    localStorage.setItem('cookiePreferences', JSON.stringify(allAccepted));
    setShowPopup(false);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      functional: false,
      advertising: false,
    };
    setPreferences(onlyNecessary);
    localStorage.setItem('cookieConsent', 'true');
    localStorage.setItem('cookiePreferences', JSON.stringify(onlyNecessary));
    setShowPopup(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookieConsent', 'true');
    localStorage.setItem('cookiePreferences', JSON.stringify(preferences));
    setShowPopup(false);
    setShowSettings(false);
  };

  const handleTogglePreference = (key) => {
    if (key === 'necessary') return; // Ne peut pas être désactivé
    setPreferences({
      ...preferences,
      [key]: !preferences[key],
    });
  };

  if (!showPopup) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm">
      {!showSettings ? (
        <div className="bg-dark-card border-2 border-gray-800 rounded-xl p-6 shadow-2xl">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center">
              <Cookie className="w-6 h-6 text-toxic-green mr-2" />
              <h3 className="text-lg font-bold text-white">Cookies</h3>
            </div>
            <button
              onClick={() => setShowPopup(false)}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <p className="text-gray-300 text-sm mb-4 leading-relaxed">
            Nous utilisons des cookies pour améliorer votre expérience, analyser l'utilisation du site et personnaliser le contenu.
          </p>

          <div className="flex flex-col gap-2 mb-4">
            <button
              onClick={handleAcceptAll}
              className="w-full px-4 py-2 bg-toxic-green text-dark-bg font-semibold rounded-lg hover:bg-opacity-80 transition-all text-sm"
            >
              Tout accepter
            </button>
            <button
              onClick={() => setShowSettings(true)}
              className="w-full px-4 py-2 border border-gray-600 text-gray-300 font-semibold rounded-lg hover:border-gray-500 transition-colors text-sm flex items-center justify-center"
            >
              <Settings className="w-4 h-4 mr-2" />
              Personnaliser
            </button>
            <button
              onClick={handleRejectAll}
              className="w-full px-4 py-2 text-gray-400 hover:text-gray-300 transition-colors text-sm"
            >
              Tout refuser
            </button>
          </div>

          <Link
            to="/cookies"
            data-discover="true"
            className="text-toxic-green hover:text-opacity-80 text-xs transition-colors"
          >
            En savoir plus
          </Link>
        </div>
      ) : (
        <div className="bg-dark-card border-2 border-gray-800 rounded-xl p-6 shadow-2xl max-h-[500px] overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white flex items-center">
              <Settings className="w-5 h-5 text-toxic-green mr-2" />
              Préférences Cookies
            </h3>
            <button
              onClick={() => setShowSettings(false)}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4 mb-4">
            <div className="flex items-center justify-between p-3 bg-dark-bg rounded-lg">
              <div>
                <p className="text-white font-semibold text-sm">Cookies Nécessaires</p>
                <p className="text-gray-400 text-xs">Toujours activés</p>
              </div>
              <div className="bg-toxic-green w-12 h-6 rounded-full flex items-center justify-end px-1">
                <div className="bg-white w-4 h-4 rounded-full"></div>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 bg-dark-bg rounded-lg">
              <div>
                <p className="text-white font-semibold text-sm">Cookies d'Analyse</p>
                <p className="text-gray-400 text-xs">Google Analytics</p>
              </div>
              <button
                onClick={() => handleTogglePreference('analytics')}
                className={`w-12 h-6 rounded-full flex items-center transition-all ${
                  preferences.analytics ? 'bg-toxic-green justify-end' : 'bg-gray-600 justify-start'
                } px-1`}
              >
                <div className="bg-white w-4 h-4 rounded-full"></div>
              </button>
            </div>

            <div className="flex items-center justify-between p-3 bg-dark-bg rounded-lg">
              <div>
                <p className="text-white font-semibold text-sm">Cookies Fonctionnels</p>
                <p className="text-gray-400 text-xs">Améliorer les fonctionnalités</p>
              </div>
              <button
                onClick={() => handleTogglePreference('functional')}
                className={`w-12 h-6 rounded-full flex items-center transition-all ${
                  preferences.functional ? 'bg-toxic-green justify-end' : 'bg-gray-600 justify-start'
                } px-1`}
              >
                <div className="bg-white w-4 h-4 rounded-full"></div>
              </button>
            </div>

            <div className="flex items-center justify-between p-3 bg-dark-bg rounded-lg">
              <div>
                <p className="text-white font-semibold text-sm">Cookies Publicitaires</p>
                <p className="text-gray-400 text-xs">Publicité ciblée</p>
              </div>
              <button
                onClick={() => handleTogglePreference('advertising')}
                className={`w-12 h-6 rounded-full flex items-center transition-all ${
                  preferences.advertising ? 'bg-toxic-green justify-end' : 'bg-gray-600 justify-start'
                } px-1`}
              >
                <div className="bg-white w-4 h-4 rounded-full"></div>
              </button>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleSavePreferences}
              className="flex-1 px-4 py-2 bg-toxic-green text-dark-bg font-semibold rounded-lg hover:bg-opacity-80 transition-all text-sm"
            >
              Enregistrer
            </button>
            <button
              onClick={() => setShowSettings(false)}
              className="flex-1 px-4 py-2 border border-gray-600 text-gray-300 font-semibold rounded-lg hover:border-gray-500 transition-colors text-sm"
            >
              Annuler
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CookieConsent;

